import { useCallback, useEffect, useState } from "react";
import { Landmark, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import api, { formatPrice } from "../lib/api";

/**
 * Phase 4.24 — Finanzierungs-Rechner-Widget.
 *
 * Annuitäten-basierte monatliche Rate mit Slidern für Anzahlung,
 * Laufzeit und Bonitäts-Klasse. Optional kombiniert mit TCO für
 * „Rate + Folgekosten = X € all-inclusive pro Monat".
 */
const BONITAT_OPTS = [
    { value: "premium", label: "Premium", hint: "3,99 % p.a.", color: "text-emerald-700" },
    { value: "standard", label: "Standard", hint: "5,49 % p.a.", color: "text-amber-700" },
    { value: "risk", label: "Risk", hint: "7,49 % p.a.", color: "text-rose-700" },
];

export default function FinancingCalculatorWidget({ listing, tcoMonthly = 0 }) {
    const [open, setOpen] = useState(false);
    const price = Number(listing?.price || 0);
    const [downPayment, setDownPayment] = useState(Math.round(price * 0.1));
    const [termMonths, setTermMonths] = useState(60);
    const [bonitat, setBonitat] = useState("standard");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const compute = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.post("/vehicles/financing", {
                price, down_payment: downPayment, term_months: termMonths, bonitat,
            });
            setResult(r.data);
        } catch {
            setResult(null);
        } finally {
            setLoading(false);
        }
    }, [price, downPayment, termMonths, bonitat]);

    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => compute(), 250);
        return () => clearTimeout(t);
    }, [open, compute]);

    if (!listing || !listing.price || listing.price < 500) return null;

    const monthlyPayment = result?.monthly_payment || 0;
    const allInclusive = monthlyPayment + tcoMonthly;
    const dpPct = price > 0 ? Math.round((downPayment / price) * 100) : 0;

    return (
        <div className="border border-gray-200 rounded-md bg-white" data-testid="financing-widget">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-2 p-4 hover:bg-gray-50 transition-colors text-left"
                data-testid="financing-toggle"
            >
                <Landmark className="w-5 h-5 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-base font-display font-bold">Finanzierungs-Rechner</div>
                    <div className="text-xs text-gray-500">Rate ab Anzahlung + Laufzeit + Bonität</div>
                </div>
                {monthlyPayment > 0 && (
                    <div className="text-right shrink-0 hidden sm:block">
                        <div className="font-display font-black text-xl text-[#111827] font-mono-num leading-none">{formatPrice(monthlyPayment)}</div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mt-0.5">Rate / Monat</div>
                    </div>
                )}
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-sm shrink-0">MOCKED</span>
                {open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>

            {open && (
                <div className="px-5 pb-5 pt-1 border-t border-gray-100" data-testid="financing-body">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 block mb-1">
                                Anzahlung: <span className="font-mono-num font-bold">{formatPrice(downPayment)}</span> ({dpPct} %)
                            </label>
                            <input
                                type="range"
                                min="0"
                                max={price}
                                step="100"
                                value={downPayment}
                                onChange={(e) => setDownPayment(Number(e.target.value))}
                                className="w-full accent-[#FFCC00]"
                                data-testid="financing-down-payment"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 block mb-1">
                                Laufzeit: <span className="font-mono-num font-bold">{termMonths} Monate</span> ({Math.round(termMonths / 12 * 10) / 10} J.)
                            </label>
                            <input
                                type="range"
                                min="12" max="120" step="6"
                                value={termMonths}
                                onChange={(e) => setTermMonths(Number(e.target.value))}
                                className="w-full accent-[#FFCC00]"
                                data-testid="financing-term"
                            />
                        </div>
                    </div>

                    <div className="mb-4">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Bonitäts-Klasse</div>
                        <div className="grid grid-cols-3 gap-2" data-testid="financing-bonitat-tiles">
                            {BONITAT_OPTS.map((o) => (
                                <button
                                    key={o.value}
                                    type="button"
                                    onClick={() => setBonitat(o.value)}
                                    className={`flex flex-col items-center justify-center p-2 rounded-sm border-2 transition-colors ${bonitat === o.value ? "border-[#111827] bg-[#FFCC00]/20" : "border-gray-200 hover:border-gray-400"}`}
                                    data-testid={`financing-bonitat-${o.value}`}
                                >
                                    <span className="text-xs font-bold uppercase tracking-wider">{o.label}</span>
                                    <span className={`text-[10px] font-mono-num ${o.color}`}>{o.hint}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex items-center gap-2 justify-center py-6 text-sm text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin" /> Berechne…
                        </div>
                    ) : result ? (
                        <>
                            <div className="grid grid-cols-3 gap-2 text-center mb-3" data-testid="financing-result">
                                <div className="bg-gray-50 border border-gray-200 rounded-sm p-2">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Rate / Monat</div>
                                    <div className="font-display font-black text-lg text-[#111827] font-mono-num">{formatPrice(monthlyPayment)}</div>
                                </div>
                                <div className="bg-gray-50 border border-gray-200 rounded-sm p-2">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Zinsen gesamt</div>
                                    <div className="font-display font-black text-lg text-[#111827] font-mono-num">{formatPrice(result.total_interest)}</div>
                                </div>
                                <div className="bg-gray-50 border border-gray-200 rounded-sm p-2">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Gesamtaufwand</div>
                                    <div className="font-display font-black text-lg text-[#111827] font-mono-num">{formatPrice(result.total_paid)}</div>
                                </div>
                            </div>

                            {tcoMonthly > 0 && (
                                <div className="bg-[#111827] text-white rounded-sm p-3 mt-3 flex items-center justify-between" data-testid="financing-all-inclusive">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-[#FFCC00]">All-inclusive pro Monat</div>
                                        <div className="text-[11px] text-gray-300">Rate + Versicherung + Sprit + Steuer + Wartung</div>
                                    </div>
                                    <div className="font-display font-black text-2xl font-mono-num text-[#FFCC00]">{formatPrice(allInclusive)}</div>
                                </div>
                            )}

                            <div className="mt-3 text-[11px] text-gray-500">{result.disclaimer}</div>
                        </>
                    ) : (
                        <div className="text-sm text-gray-500 py-4 text-center">Keine Berechnung möglich.</div>
                    )}
                </div>
            )}
        </div>
    );
}
